import { useState } from "react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLang } from "@/i18n";
import { api, fmtErr } from "@/lib/api";
import { MailQuestion, Phone, MapPin, Send } from "lucide-react";

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.success"));
      setForm({ first_name: "", last_name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "mt-1.5 bg-white border-[#E6EBF1] text-[#0A2540] focus-visible:ring-[#635BFF]/40";

  return (
    <div className="min-h-screen bg-[#F6F9FC] flex flex-col" data-testid="contact-page">
      <Navbar />
      <main className="flex-1 max-w-5xl mx-auto px-5 pt-28 pb-20 w-full">
        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <div className="w-12 h-12 rounded-xl bg-[#635BFF]/10 border border-[#635BFF]/20 flex items-center justify-center mb-5">
              <MailQuestion size={20} className="text-[#635BFF]" />
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-[#0A2540]">{t("contact.title")}</h1>
            <p className="text-[#425466] mt-3 leading-relaxed">{t("contact.subtitle")}</p>
            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-3 text-sm text-[#425466]">
                <Phone size={16} className="text-[#635BFF]" /> +41 22 555 01 40
              </div>
              <div className="flex items-center gap-3 text-sm text-[#425466]">
                <MapPin size={16} className="text-[#635BFF]" /> Geneva, Switzerland
              </div>
            </div>
          </div>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-3 bg-white border border-[#E6EBF1] rounded-2xl p-8 shadow-[0_20px_50px_rgba(10,37,64,0.08)] space-y-4"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#425466] text-xs font-medium">{t("contact.firstName")} *</Label>
                <Input data-testid="contact-firstname-input" value={form.first_name} onChange={set("first_name")} required className={inputCls} />
              </div>
              <div>
                <Label className="text-[#425466] text-xs font-medium">{t("contact.lastName")} *</Label>
                <Input data-testid="contact-lastname-input" value={form.last_name} onChange={set("last_name")} required className={inputCls} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#425466] text-xs font-medium">{t("contact.phone")} *</Label>
                <Input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} required className={inputCls} />
              </div>
              <div>
                <Label className="text-[#425466] text-xs font-medium">{t("contact.email")} *</Label>
                <Input data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} required className={inputCls} />
              </div>
            </div>
            <div>
              <Label className="text-[#425466] text-xs font-medium">{t("contact.message")} *</Label>
              <Textarea
                data-testid="contact-message-input"
                value={form.message}
                onChange={set("message")}
                required
                rows={6}
                className={`${inputCls} resize-none`}
              />
            </div>
            <Button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={loading}
              className="bg-[#635BFF] hover:bg-[#0A2540] text-white h-11 px-7 font-semibold rounded-full transition-colors"
            >
              {loading ? t("common.loading") : t("contact.send")} <Send size={15} className="ml-2" />
            </Button>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}
